/**
 * Menu Categories Admin Page
 *
 * Tammy can add, edit, reorder, and hide menu categories (e.g. Valentines, Seasonal, Gluten Free).
 * Hidden categories stay in the list but don't show on the public menu or in the menu item dropdown for new items.
 */

import { useState, useEffect } from 'react';
import { adminFetch, adminHeaders } from '../lib/adminApi';
import {
  HiPlus,
  HiPencil,
  HiTrash,
  HiEye,
  HiEyeOff,
  HiRefresh,
  HiShoppingBag,
} from 'react-icons/hi';

interface MenuCategoryRow {
  id: string;
  name: string;
  display_order: number;
  is_active: boolean;
  icon: string | null;
  created_at?: string;
  updated_at?: string;
}

const emptyForm = {
  name: '',
  display_order: 0,
  is_active: true,
  icon: '',
};

export default function MenuCategories() {
  const [categories, setCategories] = useState<MenuCategoryRow[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [catRes, countRes] = await Promise.all([
        adminFetch('/api/menu-categories'),
        adminFetch('/api/menu-categories/counts'),
      ]);
      if (!catRes.ok) throw new Error('Failed to fetch categories');
      const catData = await catRes.json();
      setCategories(catData.categories || []);
      const countData = countRes.ok ? await countRes.json() : { counts: {} };
      setCounts(countData.counts || {});
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const openNewModal = () => {
    setForm(emptyForm);
    setEditingId(null);
    setShowModal(true);
  };

  const openEditModal = (row: MenuCategoryRow) => {
    setForm({
      name: row.name,
      display_order: row.display_order,
      is_active: row.is_active,
      icon: row.icon || '',
    });
    setEditingId(row.id);
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      setError('Name is required');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const url = editingId ? `/api/menu-categories/${editingId}` : '/api/menu-categories';
      const method = editingId ? 'PUT' : 'POST';
      const res = await adminFetch(url, {
        method,
        headers: adminHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          name: form.name.trim(),
          display_order: Number(form.display_order) || 0,
          is_active: form.is_active,
          icon: form.icon.trim() || null,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Save failed');
      setShowModal(false);
      fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleHide = async (id: string) => {
    if (!window.confirm('Hide this category? It will no longer appear on the menu or in the category dropdown. Existing menu items will keep this category name.')) return;
    try {
      const res = await adminFetch(`/api/menu-categories/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Failed to hide');
      fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to hide category');
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <p className="text-gray-500">Loading categories…</p>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
          <HiShoppingBag className="w-7 h-7 text-amber-600" />
          Menu Categories
        </h1>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={fetchData}
            className="inline-flex items-center gap-1 px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            <HiRefresh className="w-4 h-4" />
            Refresh
          </button>
          <button
            type="button"
            onClick={openNewModal}
            className="inline-flex items-center gap-1 px-3 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-amber-600 hover:bg-amber-700"
          >
            <HiPlus className="w-4 h-4" />
            Add Category
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-md bg-red-50 text-red-700 text-sm">
          {error}
        </div>
      )}

      <p className="text-sm text-gray-500 mb-4">
        Categories control how items are grouped on the menu. Add new ones (e.g. Valentines, Seasonal), edit names or order, or hide categories you don’t use. Hidden categories won’t show on the public menu.
      </p>

      <div className="bg-white shadow rounded-lg overflow-hidden border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Icon</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Items</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Visible</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {categories
              .sort((a, b) => a.display_order - b.display_order)
              .map((row) => (
                <tr key={row.id} className={!row.is_active ? 'bg-gray-50' : ''}>
                  <td className="px-4 py-3 text-sm text-gray-600">{row.display_order}</td>
                  <td className="px-4 py-3 text-lg">{row.icon || '—'}</td>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{row.name}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{counts[row.name] ?? 0}</td>
                  <td className="px-4 py-3">
                    {row.is_active ? (
                      <HiEye className="w-5 h-5 text-green-600" title="Visible on menu" />
                    ) : (
                      <HiEyeOff className="w-5 h-5 text-gray-400" title="Hidden" />
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      onClick={() => openEditModal(row)}
                      className="text-amber-600 hover:text-amber-800 p-1"
                      title="Edit"
                    >
                      <HiPencil className="w-4 h-4" />
                    </button>
                    {row.is_active && (
                      <button
                        type="button"
                        onClick={() => handleHide(row.id)}
                        className="text-gray-500 hover:text-red-600 p-1 ml-1"
                        title="Hide category"
                      >
                        <HiTrash className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/30" onClick={() => setShowModal(false)} aria-hidden="true" />
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                {editingId ? 'Edit Category' : 'Add Category'}
              </h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    placeholder="e.g. Valentines"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Icon (emoji)</label>
                  <input
                    type="text"
                    value={form.icon}
                    onChange={(e) => setForm((f) => ({ ...f, icon: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    placeholder="e.g. 💝"
                  />
                </div>
                <div>
                  <label htmlFor="display_order" className="block text-sm font-medium text-gray-700 mb-1">Display order (lower = first)</label>
                  <input
                    id="display_order"
                    type="number"
                    value={form.display_order}
                    onChange={(e) => setForm((f) => ({ ...f, display_order: parseInt(e.target.value, 10) || 0 }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    aria-label="Display order (lower number shows first on menu)"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={form.is_active}
                    onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
                    className="rounded border-gray-300"
                  />
                  <label htmlFor="is_active" className="text-sm text-gray-700">Visible on menu</label>
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving || !form.name.trim()}
                  className="px-4 py-2 bg-amber-600 text-white rounded-md text-sm font-medium hover:bg-amber-700 disabled:opacity-50"
                >
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
